// Analytics service - Historical metrics and time-series data
import api from './authService';

export type TimeRange = '24h' | '48h' | '72h' | '5d' | '15d' | '30d' | '60d' | '90d';
export type Metric = 'sessions' | 'events' | 'crashes';

export interface SummaryMetrics {
  uniqueSessions: number;
  totalEvents: number;
  crashes: number;
  errors: number;
  crashFreeRate: number;
  activeApps: number;
  countries: string[];
}

export interface AnalyticsSummary {
  timeRange: TimeRange;
  startTime: string;
  endTime: string;
  summary: SummaryMetrics;
  /** Prior period of the same duration, for trend calculation */
  priorSummary: SummaryMetrics;
}

export interface EventLocation {
  lat: number;
  lng: number;
  city: string | null;
  country: string | null;
  count: number;
  apps?: Array<{
    appId: string | null;
    appName: string;
    bundleId: string | null;
    count: number;
    eventName?: string | null;
    lastEventAt?: string | null;
  }>;
}

export interface TimeSeriesData {
  timeRange: TimeRange;
  metric: Metric;
  groupBy: 'hour' | 'day';
  data: Array<{
    /** YYYY-MM-DD local date string (present when groupBy is 'day') */
    date?: string;
    timestamp: string;
    value: number;
  }>;
}

export interface CountryData {
  country: string;
  events: number;
  sessions: number;
}

export type BreakdownType =
  | 'country'
  | 'city'
  | 'device_model'
  | 'os_version'
  | 'screen_name'
  | 'screen_resolution'
  | 'locale'
  | 'timezone'
  | 'app_version'
  | 'build_number'
  | 'platform'
  | 'sdk_version'
  | 'sdk_platform';

export interface BreakdownItem {
  label: string;
  sessions: number;
  events: number;
  percentage: number;
}

export interface BreakdownResponse {
  type: BreakdownType;
  timeRange: TimeRange;
  data: BreakdownItem[];
}

export interface UserTypesResponse {
  timeRange: TimeRange;
  activeSessions: number;
  newSessions: number;
  returningSessions: number;
  newSessionPercentage: number;
}

export interface EngagementResponse {
  timeRange: TimeRange;
  totalSessions: number;
  totalEvents: number;
  avgEventsPerSession: number;
  avgSessionDurationSeconds: number | null;
}

export interface EventQueryOptions {
  appId?: string;
  startTime: string;
  endTime: string;
  eventType?: string;
  eventName?: string;
  sessionId?: string;
  /** Multi-value filters keyed by dimension; see POST /api/analytics/facets. */
  dimensions?: Record<string, string[]>;
  /** Property filters: key alone matches presence, key + value matches as text. */
  properties?: Array<{ key: string; value?: string }>;
  limit?: number;
  cursor?: string;
  cursorId?: string;
}

export interface FacetsOptions {
  appId?: string;
  startTime: string;
  endTime: string;
  dimensions?: Record<string, string[]>;
  sessionId?: string;
  properties?: Array<{ key: string; value?: string }>;
  facets?: string[];
  limit?: number;
}

export interface FacetValue {
  value: string;
  events: number;
  sessions: number;
}

export interface FacetsResponse {
  total: { events: number; sessions: number };
  propertyKeys: Array<{ value: string; events: number }>;
  facets: Record<string, FacetValue[]>;
}

export interface SeriesOptions {
  appId?: string;
  startTime: string;
  endTime: string;
  dimensions?: Record<string, string[]>;
  sessionId?: string;
  properties?: Array<{ key: string; value?: string }>;
  /** Split events by this dimension's top values plus "other". */
  splitBy?: string;
}

export interface SeriesLine {
  /** The dimension value; null for the "other" line. */
  value: string | null;
  isOther: boolean;
  events: number[];
  sessions: number[];
}

export interface SeriesResponse {
  unit: 'hour' | 'day';
  timezone: string;
  splitBy: string | null;
  /** Local bucket labels: "2026-09-18" (day) or "2026-09-18T14:00" (hour). */
  buckets: string[];
  total: { events: number[]; sessions: number[] };
  series: SeriesLine[];
}

export interface SessionEvent extends AnalyticsEvent {
  /** When it happened; for a replayed crash, the real crash time. */
  occurred_at: string;
  /** True when occurred_at differs from the reported timestamp. */
  occurred_at_adjusted: boolean;
}

export interface SessionSummary {
  startedAt: string | null;
  endedAt: string | null;
  durationSeconds: number | null;
  eventCount: number;
  countsByType: Record<string, number>;
  screenPath: string[];
  screenPathTruncated: boolean;
  crashed: boolean;
  crashReasons: string[];
  endedCleanly: boolean;
  context: {
    platform: string | null;
    deviceModel: string | null;
    osVersion: string | null;
    appVersion: string | null;
    buildNumber: string | null;
    sdkVersion: string | null;
    country: string | null;
    city: string | null;
  };
}

export interface SessionResponse {
  sessionId: string;
  truncated: boolean;
  summary: SessionSummary;
  events: SessionEvent[];
}

export interface AnalyticsEvent {
  id: string;
  event_type: string;
  event_name?: string | null;
  app_name?: string | null;
  bundle_id?: string | null;
  session_id?: string | null;
  timestamp: string;
  platform?: string | null;
  device_model?: string | null;
  os_version?: string | null;
  app_version?: string | null;
  build_number?: string | null;
  sdk_version?: string | null;
  sdk_platform?: string | null;
  country?: string | null;
  city?: string | null;
  screen_name?: string | null;
  screen_resolution?: string | null;
  locale?: string | null;
  timezone?: string | null;
  properties?: Record<string, unknown> | null;
  crash_reason?: string | null;
}

export interface EventQueryResponse {
  events: AnalyticsEvent[];
  nextCursor: string | null;
  nextCursorId: string | null;
}

export interface PortfolioComparisonRow {
  appId: string;
  appName: string;
  bundleId: string;
  platform: string;
  iconUrl: string | null;
  isActive: boolean;
  includeInPortfolio: boolean;
  sessions: number;
  events: number;
  crashes: number;
  errors: number;
  crashFreeRate: number;
  lastEventAt: string | null;
}

export interface PortfolioComparisonResponse {
  timeRange: TimeRange;
  generatedAt: string;
  lastEventAt: string | null;
  apps: PortfolioComparisonRow[];
}

class AnalyticsService {
  /**
   * Get aggregated analytics summary for a time range
   */
  async getSummary(
    timeRange: TimeRange,
    options: { appId?: string; bundleId?: string } = {}
  ): Promise<AnalyticsSummary> {
    const params = new URLSearchParams({ timeRange });
    if (options.appId) params.append('appId', options.appId);
    if (options.bundleId) params.append('bundleId', options.bundleId);

    const response = await api.get(`/api/analytics/summary?${params.toString()}`);
    return response.data;
  }

  /**
   * Get time-series data for charts.
   * Automatically sends the browser timezone so day-grouped queries
   * align with the user's local midnight rather than UTC.
   */
  async getTimeSeries(
    timeRange: TimeRange,
    metric: Metric,
    options: { appId?: string; bundleId?: string } = {}
  ): Promise<TimeSeriesData> {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const params = new URLSearchParams({ timeRange, metric, tz });
    if (options.appId) params.append('appId', options.appId);
    if (options.bundleId) params.append('bundleId', options.bundleId);

    const response = await api.get(`/api/analytics/timeseries?${params.toString()}`);
    return response.data;
  }

  /**
   * Get clustered event locations for map visualisation
   */
  async getEventLocations(
    timeRange: TimeRange,
    options: { bundleId?: string; limit?: number } = {}
  ): Promise<{ timeRange: TimeRange; locations: EventLocation[] }> {
    const params = new URLSearchParams({ timeRange });
    if (options.bundleId) params.append('bundleId', options.bundleId);
    if (options.limit) params.append('limit', options.limit.toString());

    const response = await api.get(`/api/analytics/event-locations?${params.toString()}`);
    return response.data;
  }

  /**
   * Get top countries by event count
   */
  async getTopCountries(
    timeRange: TimeRange,
    options: { appId?: string; limit?: number } = {}
  ): Promise<{ timeRange: TimeRange; countries: CountryData[] }> {
    const params = new URLSearchParams({ timeRange });
    if (options.appId) params.append('appId', options.appId);
    if (options.limit) params.append('limit', options.limit.toString());

    const response = await api.get(`/api/analytics/top-countries?${params.toString()}`);
    return response.data;
  }

  /**
   * Get top values for a dimension (country, city, device, OS version, screen name)
   */
  async getBreakdown(
    timeRange: TimeRange,
    type: BreakdownType,
    options: { bundleId?: string; limit?: number } = {}
  ): Promise<BreakdownResponse> {
    const params = new URLSearchParams({ timeRange, type });
    if (options.bundleId) params.append('bundleId', options.bundleId);
    if (options.limit)    params.append('limit', options.limit.toString());

    const response = await api.get(`/api/analytics/breakdown?${params.toString()}`);
    return response.data;
  }

  /**
   * Get new vs returning session split for a time range
   */
  async getUserTypes(
    timeRange: TimeRange,
    options: { bundleId?: string } = {}
  ): Promise<UserTypesResponse> {
    const params = new URLSearchParams({ timeRange });
    if (options.bundleId) params.append('bundleId', options.bundleId);

    const response = await api.get(`/api/analytics/user-types?${params.toString()}`);
    return response.data;
  }

  /**
   * Get average events per session and approximate session duration
   */
  async getEngagement(
    timeRange: TimeRange,
    options: { bundleId?: string } = {}
  ): Promise<EngagementResponse> {
    const params = new URLSearchParams({ timeRange });
    if (options.bundleId) params.append('bundleId', options.bundleId);

    const response = await api.get(`/api/analytics/engagement?${params.toString()}`);
    return response.data;
  }

  async getPortfolioComparison(timeRange: TimeRange): Promise<PortfolioComparisonResponse> {
    const params = new URLSearchParams({ timeRange });
    const response = await api.get(`/api/analytics/portfolio?${params.toString()}`);
    return response.data;
  }

  async queryEvents(options: EventQueryOptions): Promise<EventQueryResponse> {
    const response = await api.post('/api/analytics/query', options);
    return response.data;
  }

  async getFacets(options: FacetsOptions): Promise<FacetsResponse> {
    const response = await api.post('/api/analytics/facets', options);
    return response.data;
  }

  async getSeries(options: SeriesOptions): Promise<SeriesResponse> {
    // Buckets are cut at the viewer's local midnight / hour, as in getTimeSeries.
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const response = await api.post('/api/analytics/series', { ...options, tz });
    return response.data;
  }

  async getSession(sessionId: string, appId?: string): Promise<SessionResponse> {
    const response = await api.post('/api/analytics/session', { sessionId, ...(appId ? { appId } : {}) });
    return response.data;
  }

  /**
   * Trigger hourly aggregation (admin/cron)
   */
  async aggregateHourly(hour?: string): Promise<void> {
    await api.post('/api/analytics/aggregate-hourly', { hour });
  }

  /**
   * Trigger daily aggregation (admin/cron)
   */
  async aggregateDaily(date?: string): Promise<void> {
    await api.post('/api/analytics/aggregate-daily', { date });
  }
}

export const analyticsService = new AnalyticsService();
